import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import DashboardLayout from "@/components/dashboard/DashboardLayout";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";
import { Upload, AlertCircle } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { useNavigate } from "react-router-dom";
import { createTourAllowDuplicates } from "@/lib/toursApi";

interface JsonTour {
  id?: number | string;
  tour_code?: string;
  title?: string;
  description?: string;
  duration_hours?: number | string;
  type?: string;
  max_capacity?: number | string;
  image_url?: string;
  hcte_notes?: string;
  price_op_usd?: string | number;
  price_ag_usd?: string | number;
  price_cf_usd?: string | number;
  pricing_type?: string;
  supplier_name?: string;
  cost_supplier_usd?: string | number;
  cost_supplier_extrah_usd?: string | number;
  cost_extrah_op_usd?: string | number;
  cost_extrah_ag_usd?: string | number;
  cost_extrah_cf_usd?: string | number;
  tax?: string | number;
}

const ImportTours = () => {
  const [file, setFile] = useState<File | null>(null);
  const [importing, setImporting] = useState(false);
  const [progress, setProgress] = useState(0);
  const [results, setResults] = useState<{ 
    success: number; 
    skipped: number; 
    errors: string[];
  } | null>(null);
  const { userRole } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();

  const canImport = userRole === "ceo" || userRole === "general_manager";

  if (!canImport) {
    return (
      <DashboardLayout>
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            You don't have permission to import tours.
          </AlertDescription>
        </Alert>
      </DashboardLayout>
    );
  }

  const cleanValue = (value: any): string | null => {
    if (value === null || value === undefined) return null;
    const text = String(value).trim();
    if (!text || text === "-" || text.toLowerCase() === "null") return null;
    return text;
  };

  const parseNumber = (value: any): number | null => {
    const cleaned = cleanValue(value);
    if (!cleaned) return null;
    const num = Number(cleaned.replace(/,/g, ""));
    return Number.isNaN(num) ? null : num;
  };

  const parseId = (value: any): number | null => {
    const num = parseNumber(value);
    return num === null ? null : Math.trunc(num);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile && selectedFile.type === "application/json") {
      setFile(selectedFile);
      setResults(null);
    } else {
      toast({
        title: "Invalid file",
        description: "Please select a valid JSON file",
        variant: "destructive",
      });
    }
  };

  const handleImport = async () => {
    if (!file) return;

    setImporting(true);
    setProgress(0);
    setResults(null);

    const errors: string[] = [];

    try {
      const fileContent = await file.text();
      const parsed = JSON.parse(fileContent);
      const jsonData: JsonTour[] = Array.isArray(parsed)
        ? parsed
        : (parsed?.items || parsed?.data || parsed?.tours || []);

      if (!Array.isArray(jsonData)) {
        throw new Error("JSON file must contain an array of tours");
      }

      let success = 0;
      let skipped = 0;

      for (let i = 0; i < jsonData.length; i++) {
        const tour = jsonData[i];

        try {
          const payload = {
            id: parseId(tour.id) ?? undefined,
            tour_code: cleanValue(tour.tour_code) || "",
            title: cleanValue(tour.title) || "",
            description: cleanValue(tour.description),
            duration_hours: parseNumber(tour.duration_hours),
            type: cleanValue(tour.type) || "driver",
            pricing_type: cleanValue(tour.pricing_type) || "per_person",
            max_capacity: parseId(tour.max_capacity),
            image_url: cleanValue(tour.image_url),
            hcte_notes: cleanValue(tour.hcte_notes),
            price_op: parseNumber(tour.price_op_usd),
            price_ag: parseNumber(tour.price_ag_usd),
            price_cf: parseNumber(tour.price_cf_usd),
            supplier_name: cleanValue(tour.supplier_name),
            cost_supplier: parseNumber(tour.cost_supplier_usd),
            cost_supplier_extra_hour: parseNumber(tour.cost_supplier_extrah_usd),
            price_extra_hour_op: parseNumber(tour.cost_extrah_op_usd),
            price_extra_hour_ag: parseNumber(tour.cost_extrah_ag_usd),
            price_extra_hour_cf: parseNumber(tour.cost_extrah_cf_usd),
            tax_percentage: parseNumber(tour.tax),
          };

          await createTourAllowDuplicates(payload);
          success++;
        } catch (err: any) {
          const message = err?.message || "Unknown error";
          if (message.toLowerCase().includes("already exists") || message.toLowerCase().includes("in use")) {
            skipped++;
          } else {
            errors.push(`Row ${i + 1}: ${message}`);
          }
        }

        setProgress(((i + 1) / jsonData.length) * 100);
      }

      setResults({ success, skipped, errors });

      toast({
        title: "Import completed",
        description: `Successfully imported ${success} tours, skipped ${skipped}, failed ${errors.length}`,
      });
    } catch (error: any) {
      console.error("Import error:", error);
      toast({
        title: "Import failed",
        description: "Failed to parse or import the JSON file",
        variant: "destructive",
      });
      setResults({ success: 0, skipped: 0, errors: [error.message || "Unknown error"] });
    } finally {
      setImporting(false);
    }
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold">Import Tours</h1>
            <p className="text-muted-foreground">Import tours from JSON file</p>
          </div>
          <Button variant="outline" onClick={() => navigate("/tours")}>
            Back to Tours
          </Button>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Upload Tours JSON</CardTitle>
            <CardDescription>
              Select a JSON file containing tours data. Import will accept duplicate tour codes from the file.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="file">JSON File</Label>
              <div className="flex items-center gap-4">
                <Input
                  id="file"
                  type="file"
                  accept=".json"
                  onChange={handleFileChange}
                  disabled={importing}
                />
                <Upload className="h-5 w-5 text-muted-foreground" />
              </div>
              {file && (
                <p className="text-sm text-muted-foreground">
                  Selected: {file.name}
                </p>
              )}
            </div>

            {importing && (
              <div className="space-y-2">
                <Label>Import Progress</Label>
                <Progress value={progress} />
                <p className="text-sm text-muted-foreground">
                  {Math.round(progress)}% complete
                </p>
              </div>
            )}

            {results && (
              <Alert>
                <AlertDescription>
                  <div className="space-y-1">
                    <p><strong>Import Results:</strong></p>
                    <p>✓ Successfully imported: {results.success} tours</p>
                    <p>⊘ Skipped (already exists): {results.skipped} tours</p>
                    {results.errors.length > 0 && (
                      <div className="mt-2 max-h-40 overflow-y-auto text-xs space-y-1">
                        {results.errors.slice(0, 20).map((error, idx) => (
                          <p key={idx} className="text-destructive">{error}</p>
                        ))}
                        {results.errors.length > 20 && (
                          <p className="text-muted-foreground italic">
                            ... and {results.errors.length - 20} more errors
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                </AlertDescription>
              </Alert>
            )}

            <Button
              onClick={handleImport}
              disabled={!file || importing}
              className="w-full"
            >
              {importing ? "Importing..." : "Start Import"}
            </Button>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
};

export default ImportTours;
